/**
 * Where each surface is served. Named ONCE, here, and imported everywhere else.
 *
 * THE RULE, and it is not the obvious one. A relative link is safe when the target is served by
 * the SAME ORIGIN — not when it is the same app. landing and gtm are two separate builds that
 * ship to one origin at root paths, so `/incidents` from a landing page is correct and always
 * has been. The console and admin are separate hosts behind a key, so `/agents` from a public
 * page is not a link to the console — it is a 404 on agitome.com.
 *
 * A checker written the intuitive way ("different surface ⇒ must be absolute") would flag every
 * correct landing↔gtm link in the product and miss the two that actually break. So `crosslinks.mjs`
 * compares origins, and this file is where they are declared.
 *
 * The failure this prevents is not hypothetical: in authoxi the free tools moved from a path on
 * the marketing origin to their own host, three places were updated and two were not, and 29
 * hardcoded links broke without a single error — because the distribution answered a missing key
 * with `200 /index.html`, so every one of them returned a plausible page.
 *
 * Overridable per-build so `npm run dev` can point a surface at a local port. Unset — which is
 * every production build — they are the real hosts.
 */

import contract from "../../public-paths.json";

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

/** The public origin. landing AND gtm both ship here — see the note in public-paths.json. */
export const PUBLIC_ORIGIN = (env.VITE_PUBLIC_ORIGIN ?? contract.origins.landing).replace(/\/$/, "");

/** The host both consoles are served from. */
export const CONSOLE_ORIGIN = (env.VITE_CONSOLE_ORIGIN ?? contract.origins.console).replace(/\/$/, "");
export const ADMIN_ORIGIN = (env.VITE_ADMIN_ORIGIN ?? contract.origins.admin).replace(/\/$/, "");

/** The path each surface is mounted at on its origin. */
export const BASE_OF: Record<"landing" | "gtm" | "console" | "admin", string> =
  contract.bases as Record<"landing" | "gtm" | "console" | "admin", string>;

/**
 * The tenant console, and the staff console — same host now, different paths.
 *
 * STILL SEPARATE BUILDS, and that is the part worth keeping straight. The staff views carry
 * `adjudicate` and `leads:read`, which a tenant-owner key does not reach; a separate build means
 * that code is not in a tenant's bundle at all rather than one scope check away from working.
 * What moved is the hostname — two CloudFront behaviours on one distribution, each pointing at
 * its own S3 prefix — not the isolation.
 */
export const CONSOLE = `${CONSOLE_ORIGIN}${BASE_OF.console}`;
export const ADMIN = `${ADMIN_ORIGIN}${BASE_OF.admin}`;

/** The API. */
export const API_ORIGIN = (env.VITE_API_ORIGIN ?? contract.origins.api).replace(/\/$/, "");

/** Which surface owns a root-relative path on the public origin. */
export const OWNED: Record<"landing" | "gtm", readonly string[]> = contract.owned;

export const ORIGIN_OF = {
  landing: PUBLIC_ORIGIN,
  gtm: PUBLIC_ORIGIN,
  console: CONSOLE_ORIGIN,
  admin: ADMIN_ORIGIN,
} as const;

/**
 * The surface that owns a path, or null if nothing on the public origin does.
 *
 * Matches on the FIRST SEGMENT, so `/risk-profiles/framework/langchain` resolves to gtm the same
 * as `/risk-profiles` does.
 */
export function ownerOf(path: string): "landing" | "gtm" | null {
  const seg = "/" + path.split(/[/?#]/).filter(Boolean)[0];
  for (const [app, paths] of Object.entries(OWNED)) {
    if (paths.includes(seg)) return app as "landing" | "gtm";
  }
  return null;
}
